import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Input,
  FormGroup,
  Label,
  Alert,
  Badge,
  Row,
  Col,
  Spinner,
  Card,
  CardBody,
  CardHeader
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { FaFileInvoice, FaTruck, FaSearch, FaCheckCircle, FaClock, FaMoneyBillWave } from 'react-icons/fa';
import { formatPrice } from '../../helpers/utility';
import './DocumentSelectionModal.css'; // Assuming you have some custom styles
const DocumentSelectionModal = ({
  isOpen,
  toggle,
  typeDocument, // 'FACTURE' or 'BON_LIVRAISON'
  documents,
  loading,
  selectedDocuments,
  onDocumentsSelected
}) => {
  const { t } = useTranslation();
  const [localSelectedDocs, setLocalSelectedDocs] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!selectedDocuments) return;

    setLocalSelectedDocs(prev => {
      const same =
        prev.length === selectedDocuments.length &&
        prev.every(p => selectedDocuments.some(s => s.id === p.id));

      return same ? prev : selectedDocuments;
    });
  }, [selectedDocuments]);

  const confirmSelection = () => {
    onDocumentsSelected?.(localSelectedDocs);
  };

  useEffect(() => {
    const filteredDocs = getFilteredDocuments();
    // Only consider documents with remaining amount > 0 for the select all checkbox state
    const selectableDocs = filteredDocs.filter(doc =>
      (doc.montantTotalTTC - (doc.montantRegle || doc.montantPaye || 0)) > 0
    );

    const selectedSelectableDocs = localSelectedDocs.filter(selected =>
      selectableDocs.some(doc => doc.id === selected.id)
    );

    setSelectAll(
      selectedSelectableDocs.length > 0 &&
      selectableDocs.length > 0 &&
      selectedSelectableDocs.length === selectableDocs.length
    );
  }, [localSelectedDocs, documents, searchTerm]);

  const getFilteredDocuments = () => {
    if (!documents) return [];

    return documents.filter(doc => {
      const searchLower = searchTerm.toLowerCase();
      return (
        doc.numero?.toLowerCase().includes(searchLower) ||
        doc.reference?.toLowerCase().includes(searchLower) ||
        doc.fournisseurtNom?.toLowerCase().includes(searchLower)
      );
    });
  };

  const handleDocumentSelection = (document, isSelected) => {
    if (isSelected) {
      setLocalSelectedDocs(prev => [...prev, document]);
    } else {
      setLocalSelectedDocs(prev => prev.filter(doc => doc.id !== document.id));
    }

  };

  const handleSelectAll = () => {
    const filteredDocs = getFilteredDocuments();
    const selectableDocs = filteredDocs.filter(doc =>
      (doc.montantTotalTTC - (doc.montantPaye || doc.montantRegle || 0)) > 0
    );

    if (selectAll) {
      setLocalSelectedDocs(prev =>
        prev.filter(
          selected => !selectableDocs.some(doc => doc.id === selected.id)
        )
      );
    } else {
      const newSelections = selectableDocs.filter(
        doc => !localSelectedDocs.some(sel => sel.id === doc.id)
      );
      setLocalSelectedDocs(prev => [...prev, ...newSelections]);
    }
  };

  const calculateTotalAmount = () => {
    return localSelectedDocs.reduce((total, doc) => {
      return total + (doc.montantTotalTTC - (doc.montantRegle || doc.montantPaye || 0));
    }, 0);
  };

  const calculateStatistics = () => {
    const filteredDocs = getFilteredDocuments();
    return {
      total: filteredDocs.length,
      totalAmount: filteredDocs.reduce((sum, doc) => sum + (doc.montantTotalTTC), 0),
      selected: localSelectedDocs.length,
      selectedAmount: calculateTotalAmount(),
      unpaidAmount: filteredDocs.reduce((sum, doc) => sum + (doc.montantTotalTTC - (doc.montantRegle || doc.montantPaye)), 0)
    };
  };

  const getStatusBadge = (document) => {
    if (typeDocument === 'FACTURE') {
      switch (document.statut) {
        case 'BROUILLON':
          return <Badge color="secondary">{t('facture.brouillon')}</Badge>;
        case 'VALIDEE':
          return <Badge color="primary">{t('facture.validee')}</Badge>;
        case 'PARTIELLEMENT_PAYEE':
          return <Badge color="warning">{t('facture.partiellementPayee')}</Badge>;
        case 'PAYEE':
          return <Badge color="success">{t('facture.payee')}</Badge>;
        case 'EN_RETARD':
          return <Badge color="danger">{t('facture.enRetard')}</Badge>;
        default:
          return <Badge color="secondary">{document.statut}</Badge>;
      }
    } else {
      // Bon de livraison
      switch (document.status) {
        case 'EN_PREPARATION':
          return <Badge color="warning">{t('delivery.enpreparation')}</Badge>;
        case 'EN_COURS':
          return <Badge color="secondary">{t('delivery.encours')}</Badge>;
        case 'LIVRE':
          return <Badge color="success">{t('delivery.livre')}</Badge>;
        case 'RETOUR':
          return <Badge color="danger">{t('delivery.retourne')}</Badge>;
        default:
          return <Badge color="secondary">{document.status}</Badge>;
      }
    }
  };

  const getPaymentStatusBadge = (document) => {
    const montantPaye = document.montantRegle || document.montantPaye || 0;
    const montantTotal = document.montantTotalTTC || 0;

    if (montantPaye === 0) {
      return <Badge color="danger">{t('reglement.impaye')}</Badge>;
    } else if (montantPaye < montantTotal) {
      return <Badge color="warning">{t('reglement.partiel')}</Badge>;
    } else {
      return <Badge color="success">{t('reglement.paye')}</Badge>;
    }
  };

  const statistics = calculateStatistics();
  const filteredDocuments = getFilteredDocuments();

  return (
    <Card>
      <CardBody>
        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <div className="mt-2 text-muted">{t('common.loading')}</div>
          </div>
        ) : (
          <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <Table striped bordered hover size="sm">
              <thead className="table-light sticky-top">
                <tr>
                  <th width="50">

                    <Input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      disabled={filteredDocuments.filter(doc =>
                        ((doc.montantTotalTTC || doc.montantTTC) -
                          (doc.montantPaye || doc.montantRegle || 0)) > 0
                      ).length === 0}
                    />
                  </th>
                  <th>{t('reglement.numero')}</th>
                  <th>{t('reglement.fournisseur')}</th>
                  <th>{t('reglement.date')}</th>
                  <th>{t('reglement.montantTTC')}</th>
                  <th>{t('reglement.montantPaye')}</th>
                  <th>{t('reglement.montantRestant')}</th>
                  {typeDocument === 'FACTURE' && <th>{t('reglement.dateEcheance')}</th>}
                  <th>{t('reglement.statut')}</th>
                  <th>{t('reglement.paiement')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredDocuments.length === 0 ? (
                  <tr>
                    <td colSpan={typeDocument === 'FACTURE' ? "10" : "9"} className="text-center text-muted py-4">
                      {searchTerm ? (
                        <>
                          <FaSearch className="mb-2" />
                          <div>{t('reglement.noDocumentsFound')}</div>
                        </>
                      ) : (
                        <>
                          {typeDocument === 'FACTURE' ? (
                            <FaFileInvoice className="mb-2" />
                          ) : (
                            <FaTruck className="mb-2" />
                          )}
                          <div>
                            {typeDocument === 'FACTURE'
                              ? t('reglement.noFacturesImpayees')
                              : t('reglement.noBonLivraisonsNonRegles')}
                          </div>
                        </>
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredDocuments.map((document) => (
                    <tr key={document.id} className={localSelectedDocs.some(doc => doc.id === document.id) ? 'table-active' : ''}>                      <td>
                      {((document.montantTotalTTC || document.montantTTC) - (document.montantPaye || document.montantRegle || 0)) > 0 ? (
                        <Input
                          type="checkbox"
                          checked={localSelectedDocs.some(doc => doc.id === document.id)}
                          onChange={(e) => handleDocumentSelection(document, e.target.checked)}
                        />
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                      <td>
                        <strong>{document.numero}</strong>
                      </td>
                      <td>
                        {document.fournisseurNom || 'N/A'}
                      </td>
                      <td>
                        {document.createdAt || document.dateFacture}
                      </td>
                      <td>
                        <strong>{formatPrice(document.montantTotalTTC || document.montantTTC, false)} DH</strong>
                      </td>
                      <td>
                        {formatPrice(document.montantPaye || document.montantRegle || 0, false)} DH
                      </td>
                      <td className="fw-bold text-danger">
                        {formatPrice(

                          ((document.montantTotalTTC || document.montantTTC) - (document.montantPaye || document.montantRegle || 0)),
                          false
                        )} DH
                      </td>
                      {typeDocument === 'FACTURE' && (
                        <td>
                          {document.dateEcheance ?
                            new Date(document.dateEcheance).toLocaleDateString('fr-FR') :
                            '-'
                          }
                        </td>
                      )}
                      <td>
                        {getStatusBadge(document)}
                      </td>
                      <td>
                        {getPaymentStatusBadge(document)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        )}

        {/* Selected documents summary */}
        {localSelectedDocs.length > 0 && (
          <div className="d-flex justify-content-end mt-3 gap-2">
            <Button
              color="success"
              size='sm'
              onClick={confirmSelection}
            >
              <FaCheckCircle className="me-1" />
              {t('reglement.confirmerSelection')}
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default DocumentSelectionModal;
